import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { addDays, addMonths, format, isSameDay, parseISO, startOfMonth } from "date-fns";
import { isValidPhoneNumber } from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import { AsYouType } from "libphonenumber-js";

const formatNationalPhone = (raw, country) => {
  const digits = (raw || "").replace(/\D/g, "");
  if (!digits) return "";
  return new AsYouType(country).input(digits);
};

import WelletContext from "../../context/WelletContext";
import { setReservation } from "@/actions/reservationActions";
import { lockSchedule, lockScheduleDelete } from "@/services/reservations/reservationsService";
import { sendMessageToParent } from "@/helpers/iframe";
import {
  convertTime12to24,
  getDateFromOffset,
  getDatePerformance,
  getLocale,
} from "../../helpers/dates";
import "./VenueWidget.css";

/* ---------------- Country list ---------------- */
const COUNTRIES = [
  { code: "MX", name: "México", dial: "+52", flag: "🇲🇽" },
  { code: "US", name: "United States", dial: "+1", flag: "🇺🇸" },
  { code: "CA", name: "Canada", dial: "+1", flag: "🇨🇦" },
  { code: "AR", name: "Argentina", dial: "+54", flag: "🇦🇷" },
  { code: "BR", name: "Brasil", dial: "+55", flag: "🇧🇷" },
  { code: "CO", name: "Colombia", dial: "+57", flag: "🇨🇴" },
  { code: "CL", name: "Chile", dial: "+56", flag: "🇨🇱" },
  { code: "PE", name: "Perú", dial: "+51", flag: "🇵🇪" },
  { code: "UY", name: "Uruguay", dial: "+598", flag: "🇺🇾" },
  { code: "EC", name: "Ecuador", dial: "+593", flag: "🇪🇨" },
  { code: "VE", name: "Venezuela", dial: "+58", flag: "🇻🇪" },
  { code: "ES", name: "España", dial: "+34", flag: "🇪🇸" },
  { code: "GB", name: "United Kingdom", dial: "+44", flag: "🇬🇧" },
  { code: "FR", name: "France", dial: "+33", flag: "🇫🇷" },
  { code: "DE", name: "Deutschland", dial: "+49", flag: "🇩🇪" },
  { code: "IT", name: "Italia", dial: "+39", flag: "🇮🇹" },
  { code: "PT", name: "Portugal", dial: "+351", flag: "🇵🇹" },
];

const CountryFlag = ({ code }) => {
  const Flag = flags[code];
  return Flag ? <Flag /> : null;
};

/* ---------------- Inline SVG icons ---------------- */
const IconCalendar = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <rect x="3" y="5" width="18" height="16" rx="1" />
    <path d="M3 10h18M8 3v4M16 3v4" />
  </svg>
);
const IconClock = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);
const IconUser = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="8" r="3.5" />
    <path d="M5 21c0-3.5 3-6.5 7-6.5s7 3 7 6.5" />
  </svg>
);
const IconWarning = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 2L2 21h20L12 2z" />
    <path d="M12 9v5M12 17.5v.5" />
  </svg>
);


const OCCASIONS = [
  { value: "BIRTHDAY", labelKey: "typeOccasions.BIRTHDAY", fallback: "Cumpleaños" },
  { value: "ANNIVERSARY", labelKey: "typeOccasions.ANNIVERSARY", fallback: "Aniversario" },
  { value: "ROMANTIC_DINNER", labelKey: "typeOccasions.ROMANTIC_DINNER", fallback: "Cena Romántica" },
  { value: "BUSSINES_MEAL", labelKey: "typeOccasions.BUSSINES_MEAL", fallback: "Negocios" },
  { value: "SPECIAL_EVENT", labelKey: "typeOccasions.SPECIAL_EVENT", fallback: "Evento Especial" },
];

const DEFAULT_TIME_SLOTS = [
  "12:00", "12:30", "13:00", "13:30", "14:00", "14:30", "15:00", "15:30",
  "19:00", "19:30", "20:00", "20:30", "21:00", "21:30", "22:00", "22:30", "23:00",
];

const buildDefaultIntervals = (refDate) => {
  return DEFAULT_TIME_SLOTS.map((hm) => {
    const [h, m] = hm.split(":").map(Number);
    const d = refDate ? new Date(refDate) : new Date();
    d.setHours(h, m, 0, 0);
    return {
      time: d,
      timeMinutesStart: h * 60 + m,
      soldOut: false,
      availabilityRefId: null,
      attributes: [],
    };
  });
};

const LOCK_TIMER_SECONDS = 5 * 60;

/* ---------------- Confirmation actions ---------------- */
const addToCalendar = (conf, show) => {
  if (!conf || !show) return;
  const [h, m] = conf.time.split(":").map(Number);
  const start = new Date(conf.date);
  start.setHours(h, m, 0, 0);
  const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
  const pad = (n) => String(n).padStart(2, "0");
  const fmt = (d) =>
    `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}T${pad(
      d.getHours()
    )}${pad(d.getMinutes())}00`;
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: `${show.name} · ${conf.guests} pax`,
    dates: `${fmt(start)}/${fmt(end)}`,
    details: `Reserva ${conf.code || ""}`.trim(),
    location: show.address || "",
  });
  window.open(
    `https://www.google.com/calendar/render?${params.toString()}`,
    "_blank",
    "noopener,noreferrer"
  );
};

const openMap = (show) => {
  if (!show) return;
  if (show.latitude && show.longitude) {
    window.open(`https://www.google.com/maps/search/?api=1&query=${show.latitude},${show.longitude}`, "_blank");
  } else if (show.address) {
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(show.address)}`, "_blank");
  }
};

/* =============================================================== */
const VenueWidget = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const welletContext = useContext(WelletContext);
  const { showUrl } = useParams();

  const language = useSelector((state) => state.app.language);
  const reservation = useSelector((state) => state.reservation);
  const refCode = useSelector((state) => state.associates.refCode);
  const smId = reservation.smId;

  const locale = getLocale(language);

  /* ===== Show / loading ===== */
  const [show, setShow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [today, setToday] = useState(null);

  /* ===== Step state ===== */
  const [step, setStep] = useState(1);

  /* ===== Step 1 ===== */
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedGuests, setSelectedGuests] = useState(null);
  const [seating, setSeating] = useState("default");
  const [intervals, setIntervals] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [hasFetchedSchedule, setHasFetchedSchedule] = useState(false);
  const [slotsError, setSlotsError] = useState(null);
  const lastFetchKey = useRef(null);

  const [openPicker, setOpenPicker] = useState(null);
  const [calMonth, setCalMonth] = useState(null);

  /* ===== Step 2 ===== */
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    countryCode: "MX",
    dialCode: "+52",
    flag: "🇲🇽",
    occasion: "",
    notes: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [countdown, setCountdown] = useState(LOCK_TIMER_SECONDS);
  const [reservationToken, setReservationToken] = useState(null);
  const [lockedAttribute, setLockedAttribute] = useState(null);
  const [phoneSearch, setPhoneSearch] = useState("");

  /* ===== Step 3 ===== */
  const [confirmation, setConfirmation] = useState(null);

  const slotsGridRef = useRef(null);
  const personalDataFiredRef = useRef(false);

  /* ===== Initial show fetch ===== */
  useEffect(() => {
    document.documentElement.classList.add("widget");
    fetchShow();
    return () => {
      document.documentElement.classList.remove("widget");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchShow = async () => {
    try {
      const result = await welletContext.apis.tickets.get(
        `/shows/get/${showUrl}`,
        { params: { lang: language, details: true } }
      );
      setShow(result.data);

      const todayDate = new Date(getDateFromOffset(result.data.utcOffset));
      const minutes = todayDate.getMinutes();
      todayDate.setMinutes(
        minutes === 0 ? minutes : minutes + (15 - (minutes % 15)),
        0,
        0
      );
      setToday(todayDate);
      setCalMonth(startOfMonth(todayDate));
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  /* ===== Schedule fetch ===== */
  const fetchSchedule = async (date, pax) => {
    if (!show) return;
    setLoadingSlots(true);
    try {
      const dateStr = format(date, "yyyy-MM-dd");
      const result = await welletContext.apis.tickets.get(
        `/shows/GetSchedule?showId=${show.id}&date=${dateStr}&reservationType=RESTAURANT&paxs=${pax}`
      );
      const schedules = result.data?.schedules || [];

      const formattedTimes = schedules
        .map((time) => {
          const time24 = new Date(dateStr + "T" + convertTime12to24(time.timeStart));
          return {
            time: time24,
            timeMinutesStart: time.timeMinutesStart,
            soldOut: time.isSoldOut,
            availabilityRefId: time.availabilityRefId,
            attributes: time.attributes || [],
          };
        })
        .sort((a, b) => a.timeMinutesStart - b.timeMinutesStart);

      setIntervals(formattedTimes.filter((s) => !s.soldOut));
      setHasFetchedSchedule(true);
    } catch (err) {
      console.error(err);
      setIntervals([]);
      setHasFetchedSchedule(true);
    } finally {
      setLoadingSlots(false);
    }
  };

  useEffect(() => {
    if (!show || !selectedDate || !selectedGuests) return;
    if (!hasFetchedSchedule && !selectedTime) return;
    const key = `${format(selectedDate, "yyyy-MM-dd")}_${selectedGuests}`;
    if (lastFetchKey.current === key) return;
    lastFetchKey.current = key;
    fetchSchedule(selectedDate, selectedGuests);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, selectedDate, selectedGuests, selectedTime, hasFetchedSchedule]);

  /* ===== Countdown ===== */
  useEffect(() => {
    if (step !== 2) return;
    if (countdown <= 0) return;
    const id = setInterval(() => setCountdown((c) => c - 1), 1000);
    return () => clearInterval(id);
  }, [step, countdown]);

  /* ===== step_complete_personal_data ===== */
  useEffect(() => {
    if (step !== 2) {
      personalDataFiredRef.current = false;
      return;
    }
    if (personalDataFiredRef.current) return;

    const firstNameOk = !!form.firstName.trim() && !/\d/.test(form.firstName);
    const lastNameOk = !!form.lastName.trim() && !/\d/.test(form.lastName);
    const emailOk =
      !!form.email.trim() && /\S+@\S+\.\S+/.test(form.email);
    const fullPhone = form.dialCode + form.phone.replace(/\D/g, "");
    const phoneOk = !!form.phone.trim() && isValidPhoneNumber(fullPhone);

    if (firstNameOk && lastNameOk && emailOk && phoneOk) {
      personalDataFiredRef.current = true;
      sendProgressEvent("STEP_COMPLETE_PERSONAL_DATA", {
        extras: {
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          phone: fullPhone,
          occasion: form.occasion || null,
          notes: form.notes || null,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, form]);

  const formatCountdown = (s) => {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${String(r).padStart(2, "0")}`;
  };

  /* ===== Progress events ===== */
  const buildProgressPayload = ({ date, time, paxs, extras = {} } = {}) => {
    const effDate = date !== undefined ? date : selectedDate;
    const effTime = time !== undefined ? time : selectedTime;
    const effPaxs = paxs !== undefined ? paxs : selectedGuests;
    const dateStr = effDate ? format(effDate, "yyyy-MM-dd") : null;
    return {
      showId: show?.id ?? null,
      paxs: effPaxs ?? null,
      reservationDate: dateStr,
      reservationTime: effTime ?? null,
      reservationDateLocalTime:
        dateStr && effTime ? `${dateStr}T${effTime}` : null,
      ...extras,
    };
  };

  const sendProgressEvent = (type, opts) => {
    sendMessageToParent(window, {
      type,
      payload: buildProgressPayload(opts),
    });
  };

  /* ===== Handlers ===== */
  const handleSlotClick = (slot) => {
    const t24 = format(slot.time, "HH:mm");
    setSelectedTime(t24);
    setApiError(null);
    setSlotsError(null);

    if (selectedDate) {
      sendProgressEvent("STEP_COMPLETE_DATE", { time: t24 });
    }
  };

  const [locking, setLocking] = useState(false);

  const handleContinue = async () => {
    if (!selectedTime) return;
    const slot = filteredIntervals.find(
      (s) => format(s.time, "HH:mm") === selectedTime
    );
    if (!slot) return;

    setApiError(null);
    setSlotsError(null);
    setLocking(true);

    const performance = format(slot.time, "HH:mm");
    const seatType = slot.attributes?.includes(seating)
      ? seating
      : slot.attributes?.[0] || seating;

    const result = await lockSchedule(welletContext, {
      pax: selectedGuests,
      reservationDateLocalTime:
        format(selectedDate, "yyyy-MM-dd") + "T" + performance,
      reservationType: "RESTAURANT",
      showId: show.id,
      attribute: seatType,
    });

    const lockFailed =
      !result || result.success === false || !result.reservation_Token;

    if (lockFailed) {
      const errorType = result?.errorType;
      const isUnavailable =
        errorType === "SLOT_NOT_AVAILABLE" ||
        errorType === "SCHEDULE_SOLD_OUT" ||
        errorType === "SCHEDULE_NOT_AVAILABLE";
      setSlotsError(
        isUnavailable
          ? t(
              "widget.slotNotAvailable",
              "Este horario ya no está disponible. Por favor elegí otro horario o fecha."
            )
          : t("scheduleIsNotAvailable")
      );
      setSelectedTime(null);
      lastFetchKey.current = null;
      fetchSchedule(selectedDate, selectedGuests);
      setLocking(false);
      return;
    }

    setReservationToken(result.reservation_Token);
    setLockedAttribute(seatType);

    dispatch(
      setReservation({
        ...reservation,
        show,
        showId: show.id,
        showName: show.name,
        paxs: selectedGuests,
        date: format(selectedDate, "yyyy-MM-dd"),
        performance,
        imageUrl: show.imageUrl,
      })
    );

    sendMessageToParent(window, {
      type: "RESIZE_WIDGET",
      payload: "FULL_SCREEN",
    });

    setCountdown(LOCK_TIMER_SECONDS);
    setStep(2);
    setLocking(false);
  };

  const releaseLock = async () => {
    if (!reservationToken || !show || !selectedDate || !selectedTime) {
      setReservationToken(null);
      setLockedAttribute(null);
      return;
    }
    try {
      await lockScheduleDelete(welletContext, {
        pax: selectedGuests,
        reservationDateLocalTime:
          format(selectedDate, "yyyy-MM-dd") + "T" + selectedTime,
        reservationType: "RESTAURANT",
        showId: show.id,
        token: reservationToken,
        attribute: lockedAttribute || seating,
      });
    } catch (err) {
      console.error(err);
    }
    setReservationToken(null);
    setLockedAttribute(null);
  };

  const handleBackToStep1 = async () => {
    await releaseLock();
    setSelectedTime(null);
    setApiError(null);
    setStep(1);
  };

  const validateStep2 = () => {
    const errors = {};
    if (!form.firstName.trim()) errors.firstName = t("nameError.EMPTY_NAME");
    else if (/\d/.test(form.firstName)) errors.firstName = t("nameError.NOT_NUMBERS");
    if (!form.lastName.trim()) errors.lastName = t("nameError.EMPTY_LAST_NAME");
    else if (/\d/.test(form.lastName)) errors.lastName = t("nameError.NOT_NUMBERS");
    if (!form.email.trim()) errors.email = t("emailError.EMPTY_EMAIL");
    else if (!/\S+@\S+\.\S+/.test(form.email)) errors.email = t("emailError.INVALID_EMAIL");
    if (!form.phone.trim()) errors.phone = t("phoneError.EMPTY_PHONE_NUMBER");
    else {
      const fullPhone = form.dialCode + form.phone.replace(/\D/g, "");
      if (!isValidPhoneNumber(fullPhone))
        errors.phone = t("phoneError.INVALID_PHONE_NUMBER");
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    setApiError(null);
    if (!validateStep2()) return;
    setSubmitting(true);

    const performance = selectedTime;
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    const reservationLocalTime = getDatePerformance(dateStr, performance);
    const fullPhone = form.dialCode + form.phone.replace(/\D/g, "");

    // Fire-and-forget: dispara apenas el usuario envía la reserva, antes de
    // esperar al backend. generate_lead se dispara en BOOKING_COMPLETED.
    sendProgressEvent("RESERVATION_SUBMITTED", {
      time: performance,
      extras: {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: fullPhone,
        occasion: form.occasion || null,
        notes: form.notes || null,
      },
    });

    try {
      const requestBody = {
        showId: show.id,
        paxs: selectedGuests,
        customerName: form.firstName,
        customerLastName: form.lastName,
        customerPhone: fullPhone,
        customerLanguage: language,
        email: form.email,
        comments: form.notes ?? "",
        occasion: form.occasion || null,
        countryCode: form.countryCode,
        reservationDateLocalTime: reservationLocalTime,
        totalAmount: null,
        products: null,
        refCode,
        smId,
        oldReservationId: null,
        attribute: seating,
        hostUrlParams: welletContext.hostUrlParams,
        reservationSource: "WIDGET",
      };
      if (reservationToken) requestBody.availabilityRefId = reservationToken;

      const result = await welletContext.apis.tickets.put(
        "/reservation/generatereservation",
        requestBody
      );

      const referenceCode =
        result.data?.referenceCode ||
        result.data?.reservationCode ||
        result.data?.code ||
        "";

      setConfirmation({
        code: referenceCode,
        status: result.data?.status || "CONFIRMED",
        date: selectedDate,
        time: performance,
        guests: selectedGuests,
      });

      // Notify the host page so it can fire a conversion event (Google Ads / GA4).
      // wr-js re-emits this as a CustomEvent("wellet:booking_completed") on window.
      sendMessageToParent(window, {
        type: "BOOKING_COMPLETED",
        payload: {
          reservationCode: result.data?.reservationId,
          showId: show.id,
          showName: show.name,
          paxs: selectedGuests,
          reservationDateLocalTime:
            format(selectedDate, "yyyy-MM-dd") + "T" + performance,
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          phone: fullPhone,
        },
      });

      setStep(3);
    } catch (err) {
      const type = err?.response?.data?.errorType;
      let message;
      switch (type) {
        case "SHOW_NOT_FOUND":
          message = t("reservationError.SHOW_NOT_FOUND");
          break;
        case "SCHEDULE_SOLD_OUT":
          message = t("reservationError.SCHEDULE_SOLD_OUT");
          break;
        case "INVALID_RESERVATION_DATE":
          message = t("reservationError.INVALID_RESERVATION_DATE");
          break;
        case "DUPLICATE_RESERVATION":
          message = t("reservationError.DUPLICATE_RESERVATION");
          break;
        case "VERIFY_CUSTOMER_DATA":
          message = t("reservationError.VERIFY_CUSTOMER_DATA");
          break;
        default:
          message = t("reservationError.GENERIC_ERROR");
      }
      setApiError(message);
    } finally {
      setSubmitting(false);
    }
  };

  /* ===== Derived ===== */
  const venueBrand = useMemo(() => {
    if (!show) return "";
    const cityName = show.city?.name || "";
    return cityName ? `${show.name} · ${cityName}` : show.name;
  }, [show]);

  const upcomingDates = useMemo(() => {
    if (!today) return [];
    const arr = [];
    for (let i = 0; i < 14; i++) arr.push(addDays(today, i));
    return arr;
  }, [today]);

  const filteredCountries = useMemo(() => {
    const q = phoneSearch.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q) ||
        c.dial.includes(q)
    );
  }, [phoneSearch]);

  const pickerSourceIntervals = useMemo(() => {
    if (hasFetchedSchedule && intervals.length > 0) return intervals;
    return buildDefaultIntervals(selectedDate || today);
  }, [hasFetchedSchedule, intervals, selectedDate, today]);

  const seatingOptions = useMemo(() => {
    const present = new Set(intervals.flatMap((i) => i.attributes || []));
    const dictionary = t("translateAttribute", { returnObjects: true }) || {};
    return Object.entries(dictionary)
      .filter(([id]) => present.has(id))
      .map(([id, label]) => ({ id, label }));
  }, [intervals, t]);

  useEffect(() => {
    if (!seatingOptions.length) return;
    if (!seatingOptions.some((o) => o.id === seating)) {
      const fallback =
        seatingOptions.find((o) => o.id === "default") || seatingOptions[0];
      setSeating(fallback.id);
    }
  }, [seatingOptions, seating]);

  const pickerFilteredIntervals = useMemo(() => {
    if (!seating) return pickerSourceIntervals;
    return pickerSourceIntervals.filter(
      (s) => !s.attributes.length || s.attributes.includes(seating)
    );
  }, [pickerSourceIntervals, seating]);

  const filteredIntervals = useMemo(() => {
    if (!hasFetchedSchedule) return [];
    if (!seating) return intervals;
    return intervals.filter(
      (s) => !s.attributes.length || s.attributes.includes(seating)
    );
  }, [intervals, seating, hasFetchedSchedule]);

  useEffect(() => {
    if (!selectedTime) return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const container = slotsGridRef.current;
        if (!container) return;

        let el = container.querySelector(".slot-pill.selected");

        if (!el && filteredIntervals.length > 0) {
          const [sh, sm] = selectedTime.split(":").map(Number);
          const selectedMinutes = sh * 60 + sm;
          let closestTime = null;
          let minDiff = Infinity;
          filteredIntervals.forEach((s) => {
            const t24 = format(s.time, "HH:mm");
            const [h, m] = t24.split(":").map(Number);
            const diff = Math.abs(h * 60 + m - selectedMinutes);
            if (diff < minDiff) {
              minDiff = diff;
              closestTime = t24;
            }
          });
          if (closestTime) {
            el = container.querySelector(`[data-time="${closestTime}"]`);
          }
        }

        if (!el) return;
        const cRect = container.getBoundingClientRect();
        const eRect = el.getBoundingClientRect();
        const elOffsetInContainer = eRect.top - cRect.top + container.scrollTop;
        const target =
          elOffsetInContainer - container.clientHeight / 2 + el.offsetHeight / 2;
        const max = container.scrollHeight - container.clientHeight;
        container.scrollTo({
          top: Math.max(0, Math.min(target, max)),
          behavior: "smooth",
        });
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [selectedTime, filteredIntervals]);

  useEffect(() => {
    if (!selectedTime) return;
    if (!hasFetchedSchedule) return;
    if (filteredIntervals.length === 0) return;
    const stillAvailable = filteredIntervals.some(
      (s) => format(s.time, "HH:mm") === selectedTime
    );
    if (!stillAvailable) {
      setSelectedTime(null);
      setSlotsError(
        t(
          "widget.timeNotAvailable",
          "El horario seleccionado no está disponible. Elegí otro de los disponibles."
        )
      );
    }
  }, [filteredIntervals, selectedTime, hasFetchedSchedule, t]);

  const slotGroups = useMemo(() => {
    const lunch = [], dinner = [], late = [];
    pickerFilteredIntervals.forEach((s) => {
      const h = s.time.getHours();
      if (h < 16) lunch.push(s);
      else if (h < 22) dinner.push(s);
      else late.push(s);
    });
    return { lunch, dinner, late };
  }, [pickerFilteredIntervals]);

  /* =================================================== */
  if (loading || !show || !today) {
    return (
      <div className="vw-loading">
        <div className="vw-spinner" />
      </div>
    );
  }

  const dateChipLabel = selectedDate
    ? format(selectedDate, "EEE d MMM", { locale })
    : t("modal.dateLabel", "Fecha");
  const timeChipLabel = selectedTime
    ? format(parseISO(`2000-01-01T${selectedTime}`), "h:mm a")
    : t("modal.timeLabel", "Hora");
  const guestsChipLabel = selectedGuests
    ? `${selectedGuests} ${selectedGuests === 1 ? t("person") : t("people")}`
    : t("modal.guestsLabel", "Personas");

  return (
    <div className="vw-page">
      <div
        className="modal-backdrop"
        onClick={() => sendMessageToParent(window, { type: "CLOSE_WIDGET" })}
      />
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="modalTitle">
        <div className="modal__drag" aria-hidden="true" />

        <div className="modal__inner">
        <div className="modal__head">
          <div className="modal__head-text">
            <div className="modal__brand">{venueBrand}</div>
            <h3 className="modal__title" id="modalTitle">
              {step === 1 && t("modal.bookYourTable", "Reserva tu mesa")}
              {step === 2 && t("modal.yourDetails", "Tus datos")}
              {step === 3 && t("modal.confirmed", "Confirmado")}
            </h3>
          </div>
          <button
            className="modal__close"
            type="button"
            aria-label="Close"
            onClick={() => sendMessageToParent(window, { type: "CLOSE_WIDGET" })}
          >
            ×
          </button>
        </div>

        <div className="modal__body">
          <div className="step-progress" aria-hidden="true">
            {[1, 2, 3].map((n) => (
              <div
                key={n}
                className={`step-progress__dot ${step >= n ? "active" : ""}`}
              />
            ))}
          </div>

          {/* ============= STEP 1: WHEN ============= */}
          <div className={`modal__step ${step === 1 ? "active" : ""}`}>
            <div className="compact-row">
              <button
                type="button"
                className="compact-chip"
                onClick={() => setOpenPicker("date")}
              >
                <span className="compact-chip__icon" aria-hidden="true"><IconCalendar /></span>
                <span className={`compact-chip__value ${!selectedDate ? "placeholder" : ""}`}>
                  {dateChipLabel}
                </span>
                <span className="compact-chip__arrow">▾</span>
              </button>
              <button
                type="button"
                className="compact-chip"
                onClick={() => setOpenPicker("time")}
              >
                <span className="compact-chip__icon" aria-hidden="true"><IconClock /></span>
                <span className={`compact-chip__value ${!selectedTime ? "placeholder" : ""}`}>
                  {timeChipLabel}
                </span>
                <span className="compact-chip__arrow">▾</span>
              </button>
              <button
                type="button"
                className="compact-chip"
                onClick={() => setOpenPicker("guests")}
              >
                <span className="compact-chip__icon" aria-hidden="true"><IconUser /></span>
                <span className={`compact-chip__value ${!selectedGuests ? "placeholder" : ""}`}>
                  {guestsChipLabel}
                </span>
                <span className="compact-chip__arrow">▾</span>
              </button>
            </div>

            {seatingOptions.length > 1 ? (
              <div className="seating-tabs">
                {seatingOptions.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    className={`seating-tab ${seating === opt.id ? "selected" : ""}`}
                    onClick={() => setSeating(opt.id)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            ) : (
              <div className="seating-tabs-placeholder" aria-hidden="true" />
            )}

            <div className="slots-block">
              <div className="slots-block__label">
                {loadingSlots
                  ? t("widget.searching", "Buscando…")
                  : !hasFetchedSchedule
                  ? t("widget.completeSelection", "Completá fecha, hora y comensales")
                  : filteredIntervals.length > 0
                  ? t("widget.available", "Disponible")
                  : t("widget.noAvailability", "Sin disponibilidad")}
              </div>

              {slotsError && (
                <div className="api-error">
                  <span className="api-error__icon"><IconWarning /></span>
                  <div className="api-error__body">
                    <div className="api-error__text">{slotsError}</div>
                  </div>
                </div>
              )}

              {loadingSlots ? (
                <div className="slots-grid-inline">
                  {Array.from({ length: 9 }).map((_, i) => (
                    <div key={i} className="slot-pill slot-pill--loading" />
                  ))}
                </div>
              ) : !hasFetchedSchedule ? (
                <div className="slots-empty">
                  <span className="slots-empty__icon" aria-hidden="true"><IconClock /></span>
                  <div className="slots-empty__text">
                    {t(
                      "widget.selectAllToSeeAvailability",
                      "Elegí fecha, hora y comensales para ver la disponibilidad real."
                    )}
                  </div>
                </div>
              ) : filteredIntervals.length === 0 ? (
                <div className="slots-empty">
                  <span className="slots-empty__icon" aria-hidden="true"><IconClock /></span>
                  <div className="slots-empty__text">
                    {t(
                      "widget.noSlotsForDate",
                      "No hay horarios disponibles para esta fecha. Probá con otra fecha o ajustá la cantidad de personas."
                    )}
                  </div>
                </div>
              ) : (
                <div className="slots-grid-inline" ref={slotsGridRef}>
                  {filteredIntervals.map((slot) => {
                    const t24 = format(slot.time, "HH:mm");
                    const isSelected = selectedTime === t24;
                    return (
                      <button
                        key={slot.time.toISOString()}
                        type="button"
                        data-time={t24}
                        className={`slot-pill ${isSelected ? "selected" : ""}`}
                        onClick={() => handleSlotClick(slot)}
                      >
                        {format(slot.time, "h:mm a")}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* ============= STEP 2: WHO ============= */}
          <div className={`modal__step ${step === 2 ? "active" : ""}`}>
            <div className={`countdown ${countdown <= 0 ? "expired" : ""}`}>
              <span className="countdown__icon"><IconClock /></span>
              <div className="countdown__text">
                {countdown > 0 ? (
                  <>
                    {t("widget.holdingTable", "Apartamos esta mesa por")}{" "}
                    <span className="countdown__time">{formatCountdown(countdown)}</span> {t("widget.minutesShort", "min.")}
                  </>
                ) : (
                  <strong>{t("widget.timeExpired", "Tiempo expirado. Podemos intentar la reserva, pero no podemos garantizar el lugar.")}</strong>
                )}
              </div>
            </div>

            <button
              type="button"
              className="summary-chip"
              onClick={handleBackToStep1}
            >
              <div className="summary-chip__content">
                <div className="summary-chip__item">
                  <div className="summary-chip__label">{t("widget.date", "Fecha")}</div>
                  <div className="summary-chip__value">{dateChipLabel}</div>
                </div>
                <div className="summary-chip__item">
                  <div className="summary-chip__label">{t("widget.time", "Hora")}</div>
                  <div className="summary-chip__value">{timeChipLabel}</div>
                </div>
                <div className="summary-chip__item">
                  <div className="summary-chip__label">{t("widget.guests", "Comensales")}</div>
                  <div className="summary-chip__value">{selectedGuests}</div>
                </div>
              </div>
              <div className="summary-chip__edit">{t("widget.edit", "Editar")}</div>
            </button>

            {apiError && (
              <div className="api-error">
                <span className="api-error__icon"><IconWarning /></span>
                <div className="api-error__body">
                  <div className="api-error__text">{apiError}</div>
                  <button
                    type="button"
                    className="api-error__action"
                    onClick={handleBackToStep1}
                  >
                    {t("reschedule", "Reprogramar")}
                  </button>
                </div>
              </div>
            )}

            <div className="name-grid">
              <div className={`form-field ${formErrors.firstName ? "error" : ""}`}>
                <label className="form-field__label" htmlFor="cust-firstname">
                  <span>{t("firstName", "Nombre")}</span>
                </label>
                <input
                  id="cust-firstname"
                  type="text"
                  autoComplete="given-name"
                  autoCapitalize="words"
                  value={form.firstName}
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                />
                {formErrors.firstName && (
                  <div className="form-field__error">{formErrors.firstName}</div>
                )}
              </div>
              <div className={`form-field ${formErrors.lastName ? "error" : ""}`}>
                <label className="form-field__label" htmlFor="cust-lastname">
                  <span>{t("lastName", "Apellido")}</span>
                </label>
                <input
                  id="cust-lastname"
                  type="text"
                  autoComplete="family-name"
                  autoCapitalize="words"
                  value={form.lastName}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                />
                {formErrors.lastName && (
                  <div className="form-field__error">{formErrors.lastName}</div>
                )}
              </div>
            </div>

            <div className={`form-field ${formErrors.email ? "error" : ""}`}>
              <label className="form-field__label" htmlFor="cust-email">
                <span>{t("form.reservationEmail", "Correo")}</span>
              </label>
              <input
                id="cust-email"
                type="email"
                autoComplete="email"
                inputMode="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
              {formErrors.email && (
                <div className="form-field__error">{formErrors.email}</div>
              )}
            </div>

            <div className={`form-field ${formErrors.phone ? "error" : ""}`}>
              <label className="form-field__label" htmlFor="cust-phone">
                <span>{t("form.phoneNumber", "Teléfono")}</span>
              </label>
              <div className="phone-input">
                <button
                  type="button"
                  className="phone-country"
                  aria-label="Select country code"
                  onClick={() => setOpenPicker("phone")}
                >
                  <span className="phone-flag"><CountryFlag code={form.countryCode} /></span>
                  <span className="phone-code">{form.dialCode}</span>
                  <span className="phone-arrow">▼</span>
                </button>
                <input
                  id="cust-phone"
                  type="tel"
                  autoComplete="tel-national"
                  inputMode="tel"
                  value={form.phone}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      phone: formatNationalPhone(e.target.value, form.countryCode),
                    })
                  }
                />
              </div>
              {formErrors.phone && (
                <div className="form-field__error">{formErrors.phone}</div>
              )}
            </div>

            <div className="extras-grid">
              <div className="form-field">
                <label className="form-field__label" htmlFor="modal-occasion">
                  <span>{t("occasion", "Ocasión")}</span>
                  <span className="optional">{t("widget.optional", "opcional")}</span>
                </label>
                <select
                  id="modal-occasion"
                  value={form.occasion}
                  onChange={(e) => setForm({ ...form, occasion: e.target.value })}
                >
                  <option value="">{t("widget.selectOccasion", "Selecciona una ocasión")}</option>
                  {OCCASIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {t(o.labelKey, o.fallback)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label className="form-field__label" htmlFor="cust-notes">
                  <span>{t("specialRequests", "Solicitud especial")}</span>
                  <span className="optional">{t("widget.optional", "opcional")}</span>
                </label>
                <input
                  id="cust-notes"
                  type="text"
                  placeholder={t("widget.specialRequestsPlaceholder", "Alergias, dieta, preferencias…")}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* ============= STEP 3: CONFIRMATION ============= */}
          <div className={`modal__step ${step === 3 ? "active" : ""}`}>
            {confirmation && (
              <div className={`confirmation ${confirmation.status === "PENDING" ? "confirmation--pending" : ""}`}>
                <div className="confirmation__check-wrap">
                  <div className="confirmation__check">
                    {confirmation.status === "PENDING" ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 7v5l3 2" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <polyline points="5 12 10 17 19 7" />
                      </svg>
                    )}
                  </div>
                </div>
                {confirmation.status === "PENDING" ? (
                  <>
                    <h3 className="confirmation__title">{t("widget.processingReservation", "Procesando tu reserva")}</h3>
                    <p className="confirmation__sub">{t("processingRequest")}</p>
                  </>
                ) : (
                  <>
                    <h3 className="confirmation__title">{t("widget.allSet", "¡Listo!")}</h3>
                    <p className="confirmation__sub">{t("widget.happyToHost", "Estamos felices de recibirte.")}</p>
                  </>
                )}

                <div className="confirmation__details">
                  <div className="confirmation__detail-row">
                    <span className="confirmation__detail-label">{t("widget.confirmationCode", "Confirmación")}</span>
                    <span className="confirmation__detail-value confirmation__detail-value--code">
                      {confirmation.code || "—"}
                    </span>
                  </div>
                  <div className="confirmation__detail-row">
                    <span className="confirmation__detail-label">{t("widget.date", "Fecha")}</span>
                    <span className="confirmation__detail-value">
                      {format(confirmation.date, "EEE, d MMM yyyy", { locale })}
                    </span>
                  </div>
                  <div className="confirmation__detail-row">
                    <span className="confirmation__detail-label">{t("widget.time", "Hora")}</span>
                    <span className="confirmation__detail-value">
                      {format(parseISO(`2000-01-01T${confirmation.time}`), "h:mm a")}
                    </span>
                  </div>
                  <div className="confirmation__detail-row">
                    <span className="confirmation__detail-label">{t("widget.guests", "Comensales")}</span>
                    <span className="confirmation__detail-value">{confirmation.guests}</span>
                  </div>
                </div>

                <div className="confirmation__actions">
                  {confirmation.status !== "PENDING" && (
                    <button
                      type="button"
                      className="confirmation__action"
                      onClick={() => addToCalendar(confirmation, show)}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <rect x="3" y="5" width="18" height="16" rx="1" />
                        <path d="M3 9h18M8 3v4M16 3v4" />
                      </svg>
                      <span>{t("widget.calendar", "Calendario")}</span>
                    </button>
                  )}
                  <button
                    type="button"
                    className="confirmation__action"
                    onClick={() => openMap(show)}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M12 2C8 2 5 5 5 9c0 5 7 13 7 13s7-8 7-13c0-4-3-7-7-7z" />
                      <circle cx="12" cy="9" r="2" />
                    </svg>
                    <span>{t("widget.map", "Mapa")}</span>
                  </button>
                </div>

                <p className="confirmation__email-note">
                  {confirmation.status === "PENDING"
                    ? t("widget.willNotifyByEmail", "Te avisaremos por correo apenas se confirme.")
                    : t("widget.sentByEmail", "Te enviamos la confirmación por correo.")}
                </p>
              </div>
            )}
          </div>

          {/* ===== Picker overlays ===== */}
          <DatePicker
            open={openPicker === "date"}
            onClose={() => setOpenPicker(null)}
            today={today}
            upcoming={upcomingDates}
            calMonth={calMonth}
            setCalMonth={setCalMonth}
            selectedDate={selectedDate}
            onSelect={(d) => {
              setSelectedDate(d);
              setSlotsError(null);
              setOpenPicker(null);

              if (selectedTime) {
                sendProgressEvent("STEP_COMPLETE_DATE", { date: d });
              }
            }}
            locale={locale}
          />

          <TimePicker
            open={openPicker === "time"}
            onClose={() => setOpenPicker(null)}
            groups={slotGroups}
            selectedTime={selectedTime}
            onSelect={(slot) => {
              setOpenPicker(null);
              handleSlotClick(slot);
            }}
          />

          <GuestsPicker
            open={openPicker === "guests"}
            onClose={() => setOpenPicker(null)}
            value={selectedGuests}
            onSelect={(n) => {
              setSelectedGuests(n);
              setSlotsError(null);
              setOpenPicker(null);

              sendProgressEvent("STEP_COMPLETE_GUESTS", { paxs: n });
            }}
          />

          <PhonePicker
            open={openPicker === "phone"}
            onClose={() => setOpenPicker(null)}
            search={phoneSearch}
            setSearch={setPhoneSearch}
            countries={filteredCountries}
            selectedCode={form.countryCode}
            onSelect={(c) => {
              setForm({
                ...form,
                countryCode: c.code,
                dialCode: c.dial,
                flag: c.flag,
                phone: formatNationalPhone(form.phone, c.code),
              });
              setOpenPicker(null);
              setPhoneSearch("");
            }}
          />
        </div>

        <div className="modal__footer">
        <div
          className={`step-footer ${step === 1 ? "active" : ""}`}
          data-step-footer="1"
        >
          <button
            type="button"
            className="btn-primary"
            disabled={
              !selectedDate ||
              !selectedTime ||
              !selectedGuests ||
              !hasFetchedSchedule ||
              filteredIntervals.length === 0 ||
              loadingSlots ||
              locking
            }
            onClick={handleContinue}
          >
            {locking ? t("form.processing", "Procesando…") : t("continue", "Continuar")}
          </button>
        </div>
        <div
          className={`step-footer ${step === 2 ? "active" : ""}`}
          data-step-footer="2"
        >
          <div className="step-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleBackToStep1}
            >
              {t("widget.back", "Volver")}
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={submitting || countdown <= 0}
              onClick={handleSubmit}
            >
              {submitting ? t("form.processing", "Procesando…") : t("widget.confirm", "Confirmar")}
            </button>
          </div>
        </div>
        <div
          className={`step-footer ${step === 3 ? "active" : ""}`}
          data-step-footer="3"
        >
          <button
            type="button"
            className="btn-primary"
            onClick={() =>
              sendMessageToParent(window, { type: "CLOSE_WIDGET" })
            }
          >
            {t("done", "Listo")}
          </button>
        </div>
          <div className="powered-by">
            <span>Powered by</span> <strong>OpenTable</strong>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

/* =============================================================
   Subcomponents
   ============================================================= */

const DatePicker = ({
  open,
  onClose,
  today,
  upcoming,
  calMonth,
  setCalMonth,
  selectedDate,
  onSelect,
  locale,
}) => {
  const { t } = useTranslation();
  if (!calMonth || !today) return null;
  const monthStart = calMonth;
  const year = monthStart.getFullYear();
  const month = monthStart.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const offset = (firstDay + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const canGoPrev =
    monthStart.getFullYear() > today.getFullYear() ||
    (monthStart.getFullYear() === today.getFullYear() &&
      monthStart.getMonth() > today.getMonth());

  const todayStart = new Date(today);
  todayStart.setHours(0, 0, 0, 0);

  return (
    <div className={`picker-overlay ${open ? "open" : ""}`}>
      <div className="picker-head">
        <button className="picker-back" type="button" onClick={onClose} aria-label="Back">
          ←
        </button>
        <h4 className="picker-title">{t("widget.chooseDate", "Elige la fecha")}</h4>
      </div>
      <div className="picker-body">
        <div className="picker-section">
          <div className="picker-section__label">{t("widget.upcomingDays", "Próximos días")}</div>
          <div className="date-row">
            {upcoming.map((d) => {
              const isSel = selectedDate && isSameDay(d, selectedDate);
              const weekday = isSameDay(d, today)
                ? t("widget.todayShort", "Hoy")
                : isSameDay(d, addDays(today, 1))
                ? t("widget.tomorrowShort", "Mañ")
                : format(d, "EEE", { locale });
              return (
                <button
                  key={d.toISOString()}
                  type="button"
                  className={`date-pill ${isSel ? "selected" : ""}`}
                  onClick={() => onSelect(d)}
                >
                  <span className="date-pill__weekday">{weekday}</span>
                  <span className="date-pill__day">{format(d, "d")}</span>
                  <span className="date-pill__month">{format(d, "MMM", { locale })}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="picker-section">
          <div className="picker-section__label">{t("widget.calendarTitle", "Calendario")}</div>
          <div className="calendar">
            <div className="calendar-head">
              <button
                type="button"
                className="calendar-nav"
                disabled={!canGoPrev}
                onClick={() => setCalMonth(addMonths(monthStart, -1))}
                aria-label="Previous month"
              >
                ‹
              </button>
              <div className="calendar-title">
                {format(monthStart, "MMMM yyyy", { locale })}
              </div>
              <button
                type="button"
                className="calendar-nav"
                onClick={() => setCalMonth(addMonths(monthStart, 1))}
                aria-label="Next month"
              >
                ›
              </button>
            </div>
            <div className="calendar-weekdays">
              {["L", "M", "M", "J", "V", "S", "D"].map((d, i) => (
                <span key={i}>{d}</span>
              ))}
            </div>
            <div className="calendar-grid">
              {cells.map((cellDate, i) => {
                if (!cellDate) {
                  return <div key={i} className="cal-cell cal-cell--empty" />;
                }
                const past = cellDate < todayStart;
                const isToday = isSameDay(cellDate, today);
                const isSel = selectedDate && isSameDay(cellDate, selectedDate);
                let cls = "cal-cell";
                if (past) cls += " cal-cell--past";
                if (isToday && !isSel) cls += " cal-cell--today";
                if (isSel) cls += " cal-cell--selected";
                return (
                  <button
                    key={i}
                    type="button"
                    disabled={past}
                    className={cls}
                    onClick={() => onSelect(cellDate)}
                  >
                    {format(cellDate, "d")}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const TimePicker = ({ open, onClose, groups, selectedTime, onSelect }) => {
  const { t } = useTranslation();
  const bodyRef = useRef(null);

  useEffect(() => {
    if (!open || !selectedTime) return;
    const id = setTimeout(() => {
      const body = bodyRef.current;
      if (!body) return;

      let targetEl = body.querySelector(".slot-pill.selected");

      if (!targetEl) {
        const allSlots = [...groups.lunch, ...groups.dinner, ...groups.late];
        if (allSlots.length === 0) return;

        const [sh, sm] = selectedTime.split(":").map(Number);
        const selectedMinutes = sh * 60 + sm;

        let closestTime = null;
        let minDiff = Infinity;
        allSlots.forEach((s) => {
          const t24 = format(s.time, "HH:mm");
          const [h, m] = t24.split(":").map(Number);
          const diff = Math.abs(h * 60 + m - selectedMinutes);
          if (diff < minDiff) {
            minDiff = diff;
            closestTime = t24;
          }
        });

        if (closestTime) {
          targetEl = body.querySelector(`[data-time="${closestTime}"]`);
        }
      }

      if (!targetEl) return;

      let offset = 0;
      let node = targetEl;
      while (node && node !== body) {
        offset += node.offsetTop;
        node = node.offsetParent;
      }
      const target = offset - body.clientHeight / 2 + targetEl.offsetHeight / 2;
      const max = body.scrollHeight - body.clientHeight;
      body.scrollTo({
        top: Math.max(0, Math.min(target, max)),
        behavior: "smooth",
      });
    }, 50);
    return () => clearTimeout(id);
  }, [open, selectedTime, groups]);

  const renderGroup = (label, slots) => {
    if (!slots.length) return null;
    return (
      <div className="all-times-section" key={label}>
        <div className="all-times-section__label">{label}</div>
        <div className="slots-grid-inline">
          {slots.map((s) => {
            const t24 = format(s.time, "HH:mm");
            const isSel = selectedTime === t24;
            return (
              <button
                key={t24}
                type="button"
                data-time={t24}
                className={`slot-pill ${isSel ? "selected" : ""}`}
                onClick={() => onSelect(s)}
              >
                {format(s.time, "h:mm a")}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className={`picker-overlay ${open ? "open" : ""}`}>
      <div className="picker-head">
        <button className="picker-back" type="button" onClick={onClose} aria-label="Back">
          ←
        </button>
        <h4 className="picker-title">{t("widget.chooseTime", "Elige la hora")}</h4>
      </div>
      <div className="picker-body" ref={bodyRef}>
        {renderGroup(t("widget.lunch", "Comida"), groups.lunch)}
        {renderGroup(t("widget.dinner", "Cena"), groups.dinner)}
        {renderGroup(t("widget.lateNight", "Después de cena"), groups.late)}
        {!groups.lunch.length && !groups.dinner.length && !groups.late.length && (
          <div className="slots-empty">
            <span className="slots-empty__icon"><IconClock /></span>
            <div className="slots-empty__text">
              {t("widget.noSlotsTryAnother", "No hay horarios disponibles. Probá con otra fecha.")}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const GuestsPicker = ({ open, onClose, value, onSelect }) => {
  const { t } = useTranslation();
  const [stepperValue, setStepperValue] = useState(Math.max(value || 9, 9));
  const [showStepper, setShowStepper] = useState((value || 0) > 8);

  const adjust = (delta) => {
    const next = Math.max(9, Math.min(15, stepperValue + delta));
    setStepperValue(next);
    onSelect(next);
  };

  return (
    <div className={`picker-overlay ${open ? "open" : ""}`}>
      <div className="picker-head">
        <button className="picker-back" type="button" onClick={onClose} aria-label="Back">
          ←
        </button>
        <h4 className="picker-title">{t("widget.howManyGuests", "Cuántas personas")}</h4>
      </div>
      <div className="picker-body">
        <div className="guests-row">
          {[2, 3, 4, 5, 6, 7, 8].map((n) => (
            <button
              key={n}
              type="button"
              className={`guests-pill ${value === n && !showStepper ? "selected" : ""}`}
              onClick={() => {
                setShowStepper(false);
                onSelect(n);
              }}
            >
              {n}
            </button>
          ))}
          <button
            type="button"
            className={`guests-pill ${showStepper ? "selected" : ""}`}
            onClick={() => {
              setShowStepper(true);
              const v = Math.max(value || 9, 9);
              setStepperValue(v);
              onSelect(v);
            }}
          >
            9+
          </button>
        </div>
        {showStepper && (
          <div className="guests-stepper">
            <button
              type="button"
              className="guests-stepper__btn"
              disabled={stepperValue <= 9}
              onClick={() => adjust(-1)}
            >
              −
            </button>
            <span className="guests-stepper__value">{stepperValue}</span>
            <button
              type="button"
              className="guests-stepper__btn"
              disabled={stepperValue >= 15}
              onClick={() => adjust(1)}
            >
              +
            </button>
            <span className="guests-stepper__label">{t("widget.peopleLabel", "Personas")}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const PhonePicker = ({ open, onClose, search, setSearch, countries, selectedCode, onSelect }) => {
  const { t } = useTranslation();
  return (
  <div className={`phone-country-picker ${open ? "open" : ""}`}>
    <div className="phone-picker-head">
      <button className="phone-picker-back" type="button" onClick={onClose} aria-label="Back">
        ←
      </button>
      <input
        type="search"
        className="phone-picker-search"
        placeholder={t("widget.searchCountry", "Buscar país")}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
    </div>
    <div className="phone-picker-list">
      {countries.map((c) => (
        <button
          key={c.code}
          type="button"
          className={`phone-country-option ${selectedCode === c.code ? "selected" : ""}`}
          onClick={() => onSelect(c)}
        >
          <span className="opt-flag"><CountryFlag code={c.code} /></span>
          <span className="opt-name">{c.name}</span>
          <span className="opt-code">{c.dial}</span>
        </button>
      ))}
    </div>
  </div>
  );
};

export default VenueWidget;
